/**
 * OdooService for Poker Reborn
 *
 * Thin REST client for Odoo API calls (authentication, onboarding, profile).
 * Manages session token lifecycle — obtain, store, refresh, use.
 *
 * @see docs/protocols/AUTH.md - Authentication flow, token exchange
 */

import { log } from '@/utils/log';
import type { AuthTokenResponse, OnboardingResponse } from '@/types/odoo';
import type { GuestAuthResponse, ValidateResponse, OdooAuthResponse, LinkAuthResponse } from '@/types/auth';

// =============================================================================
// CONSTANTS
// =============================================================================

const SESSION_TOKEN_KEY = 'poker_session_token';
const TOKEN_EXPIRES_KEY = 'poker_token_expires_at';

const API_PREFIX = '/poker/api/v1';

// =============================================================================
// SERVICE
// =============================================================================

class OdooService {
  private static instance: OdooService | null = null;

  private sessionToken: string | null = null;
  private tokenExpiresAt: string | null = null;
  private readonly baseUrl: string;
  private readonly apiKey: string | undefined;

  private constructor() {
    this.baseUrl = (import.meta.env.VITE_ODOO_API_URL as string | undefined) ?? 'http://localhost:8069';
    this.apiKey = import.meta.env.VITE_ODOO_API_KEY as string | undefined;

    // Restore token from sessionStorage (survives page refresh)
    this.restoreToken();
  }

  static getInstance(): OdooService {
    if (!OdooService.instance) {
      OdooService.instance = new OdooService();
    }
    return OdooService.instance;
  }

  // ===========================================================================
  // TOKEN MANAGEMENT
  // ===========================================================================

  /** Set session token (from Google OAuth or /auth/token response) */
  setSessionToken(token: string, expiresAt?: string): void {
    this.sessionToken = token;
    this.tokenExpiresAt = expiresAt ?? null;

    sessionStorage.setItem(SESSION_TOKEN_KEY, token);
    if (expiresAt) {
      sessionStorage.setItem(TOKEN_EXPIRES_KEY, expiresAt);
    }

    log.odoo.info`Session token stored (expires: ${expiresAt ?? 'unknown'})`;
    if (import.meta.env.DEV) {
       
      console.log('[odoo] Session token:', token);
    }
  }

  /** Get current session token (null if missing or expired) */
  getSessionToken(): string | null {
    if (!this.sessionToken) return null;

    if (this.isTokenExpired()) {
      log.odoo.info`Session token expired, clearing`;
      this.clearToken();
      return null;
    }

    return this.sessionToken;
  }

  /** Check if we have a valid (non-expired) token */
  hasValidToken(): boolean {
    return this.getSessionToken() !== null;
  }

  /** Clear stored token */
  clearToken(): void {
    this.sessionToken = null;
    this.tokenExpiresAt = null;
    sessionStorage.removeItem(SESSION_TOKEN_KEY);
    sessionStorage.removeItem(TOKEN_EXPIRES_KEY);
  }

  // ===========================================================================
  // API METHODS
  // ===========================================================================

  /**
   * Exchange username/password for a session token.
   * Used for URL param login (?user=&pass=) where no Google OAuth happens.
   *
   * Non-blocking — logs and swallows errors. The app works without a token
   * (just can't call /me/* endpoints).
   */
  async authenticate(username: string, password: string): Promise<void> {
    // Always get a fresh token — stored tokens may be invalidated server-side
    // by another login, so trusting them just causes a 401 round-trip.
    this.clearToken();

    try {
      log.odoo.info`Authenticating ${username} via POST /auth/token...`;

      const data = await this.post<AuthTokenResponse>('/auth/token', {
        username,
        password,
      });

      if (data.success && data.session_token) {
        this.setSessionToken(data.session_token, data.token_expires_at);
      } else {
        log.odoo.warning`Auth token request failed: ${data.error ?? 'unknown error'}`;
      }
    } catch (error) {
      // Non-blocking — log and continue
      const message = error instanceof Error ? error.message : String(error);
      log.odoo.warning`Failed to get session token: ${message}`;
    }
  }

  /**
   * Fetch onboarding configuration from Odoo.
   * Returns null on any failure (non-blocking — app proceeds to lobby).
   */
  async getOnboarding(): Promise<OnboardingResponse | null> {
    if (!this.hasValidToken()) {
      log.odoo.debug`No session token, skipping onboarding fetch`;
      return null;
    }

    try {
      log.odoo.info`Fetching onboarding config...`;
      const data = await this.get<OnboardingResponse>('/me/onboarding');

      if (data.success) {
        log.odoo.info`Onboarding: type=${data.onboarding.onboarding_type}, sessions=${String(data.onboarding.sessions)}`;
        return data;
      }

      log.odoo.warning`Onboarding request returned success=false`;
      return null;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      log.odoo.warning`Failed to fetch onboarding: ${message}`;
      return null;
    }
  }

  // ===========================================================================
  // AUTH ENDPOINTS
  // ===========================================================================

  /** Create a guest account via Odoo. Native only (device_id required). */
  async createGuest(deviceId: string): Promise<GuestAuthResponse> {
    return this.post<GuestAuthResponse>('/auth/guest', { device_id: deviceId });
  }

  /** Validate stored credentials (lightweight check before connecting). */
  async validateCredentials(username: string, password: string): Promise<ValidateResponse> {
    return this.post<ValidateResponse>('/auth/validate', { username, password });
  }

  /** Social login — new or returning user (no existing guest account). */
  async socialLogin(provider: string, token: string, deviceId?: string | null): Promise<OdooAuthResponse> {
    const tokenKey = provider === 'facebook' ? `${provider}_access_token` : `${provider}_id_token`;
    const body: Record<string, string> = {
      [tokenKey]: token,
    };
    if (deviceId) body['device_id'] = deviceId;
    return this.post<OdooAuthResponse>(`/auth/${provider}`, body);
  }

  /** Link a social provider to an existing guest account. */
  async socialLink(
    provider: string,
    token: string,
    username: string,
    password: string,
    deviceId?: string | null,
  ): Promise<LinkAuthResponse> {
    const tokenKey = provider === 'facebook' ? `${provider}_access_token` : `${provider}_id_token`;
    const body: Record<string, string> = {
      [tokenKey]: token,
      username,
      password,
    };
    if (deviceId) body['device_id'] = deviceId;
    return this.post<LinkAuthResponse>(`/auth/${provider}/link`, body);
  }

  // ===========================================================================
  // HTTP INTERNALS
  // ===========================================================================

  /** Authenticated GET request (includes Bearer token) */
  private async get<T>(path: string): Promise<T> {
    const token = this.getSessionToken();
    if (!token) {
      throw new Error('No valid session token for authenticated request');
    }

    const headers = this.buildHeaders();
    headers['Authorization'] = `Bearer ${token}`;

    const response = await fetch(`${this.baseUrl}${API_PREFIX}${path}`, {
      method: 'GET',
      headers,
    });

    if (response.status === 401) {
      // Token was rejected by server (purged by new login, expired server-side, etc.)
      log.odoo.warning`Token rejected by server (401), clearing stale token`;
      this.clearToken();
      throw new Error(`GET ${path} failed: 401 UNAUTHORIZED (token cleared)`);
    }

    if (!response.ok) {
      throw new Error(`GET ${path} failed: ${String(response.status)} ${response.statusText}`);
    }

    return (await response.json()) as T;
  }

  /** Unauthenticated POST request (uses API key only) */
  private async post<T>(path: string, body: unknown): Promise<T> {
    const headers = this.buildHeaders();
    headers['Content-Type'] = 'application/json';

    const response = await fetch(`${this.baseUrl}${API_PREFIX}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`POST ${path} failed: ${String(response.status)} ${response.statusText}`);
    }

    return (await response.json()) as T;
  }

  /** Build common headers (API key if configured) */
  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    }
    return headers;
  }

  // ===========================================================================
  // PRIVATE HELPERS
  // ===========================================================================

  /** Restore token from sessionStorage */
  private restoreToken(): void {
    const storedToken = sessionStorage.getItem(SESSION_TOKEN_KEY);
    const storedExpiry = sessionStorage.getItem(TOKEN_EXPIRES_KEY);

    if (storedToken) {
      this.sessionToken = storedToken;
      this.tokenExpiresAt = storedExpiry;

      if (this.isTokenExpired()) {
        log.odoo.debug`Restored token is expired, clearing`;
        this.clearToken();
      } else {
        log.odoo.debug`Restored session token from sessionStorage`;
      }
    }
  }

  /** Check if token has expired */
  private isTokenExpired(): boolean {
    if (!this.tokenExpiresAt) return false; // No expiry known — assume valid

    const expiresAt = new Date(this.tokenExpiresAt).getTime();
    const now = Date.now();
    // Consider expired 60 seconds early to avoid edge cases
    return now >= expiresAt - 60_000;
  }
}

export const odooService = OdooService.getInstance();
